import { DialogComponent } from '@theme/dialog';
import { ThemeEvents, CartAddEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

class BulkPricingModal extends DialogComponent {
  requiredRefs = ['dialog'];

  #tiers = [];
  #dialogRefs = {};

  connectedCallback() {
    super.connectedCallback();

    const { dialog } = this.refs;
    
    if (dialog) {
      this.#cacheDialogRefs(dialog);
      
      if (dialog.parentNode !== document.body) {
        document.body.appendChild(dialog);
      }
      
      this.#bindDialogEvents(dialog);
    }

    try {
      const tiersData = this.dataset.tiers;
      if (tiersData) {
        this.#tiers = JSON.parse(tiersData);
        this.#tiers.sort((a, b) => b.min - a.min);
      }
    } catch (e) {}

    const target = this.closest('.shopify-section, dialog');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
  }

  #cacheDialogRefs(dialog) {
    this.#dialogRefs = {
      qtyInput: dialog.querySelector('.bulk-pricing-modal__qty-val'),
      cartButton: dialog.querySelector('.bulk-pricing-modal__cart-btn'),
      variantInput: dialog.querySelector('input[name="id"]'),
      unitPriceDisplay: dialog.querySelector('[ref="unitPriceDisplay"]'),
      totalPriceDisplay: dialog.querySelector('[ref="totalPriceDisplay"]'),
      moneyFormat: dialog.querySelector('[ref="moneyFormat"]'),
      stockCount: dialog.querySelector('[ref="stockCount"]'),
      tierTableBody: dialog.querySelector('[ref="tierTableBody"]'),
    };
  }

  get dialogRefs() {
    return {
      ...this.refs,
      ...this.#dialogRefs,
    };
  }

  #bindDialogEvents(dialog) {
    if (dialog._bulkPricingEventsbound) return;
    dialog._bulkPricingEventsbound = true;
    
    dialog.addEventListener('click', (e) => {
      if (e.target === dialog) {
        this.closeDialog();
      }
    });

    dialog.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        this.closeDialog();
      }
    });

    const closeBtn = dialog.querySelector('.bulk-pricing-modal__close');
    if (closeBtn) {
      closeBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.closeDialog();
      });
    }

    const qtyBtns = dialog.querySelectorAll('.bulk-pricing-modal__qty-btn');
    qtyBtns.forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        if (btn.textContent.trim() === '−') {
          this.decreaseQty();
        } else if (btn.textContent.trim() === '+') {
          this.increaseQty();
        }
      });
    });

    const qtyInput = dialog.querySelector('.bulk-pricing-modal__qty-val');
    if (qtyInput) {
      qtyInput.addEventListener('input', () => this.updateCartButton());
      qtyInput.addEventListener('change', () => this.updateCartButton());
    }

    const cartBtn = dialog.querySelector('.bulk-pricing-modal__cart-btn');
    if (cartBtn) {
      cartBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.addToCart();
      });
    }
  }

  showDialog() {
    super.showDialog();
    
    const dialog = this.refs.dialog;
    
    if (dialog) {
      this.#cacheDialogRefs(dialog);
    }
    
    const cartButton = this.#dialogRefs.cartButton || dialog?.querySelector('.bulk-pricing-modal__cart-btn');
    const qtyInput = this.#dialogRefs.qtyInput || dialog?.querySelector('.bulk-pricing-modal__qty-val');
    
    if (cartButton) {
      cartButton.disabled = false;
    }
    
    if (qtyInput) {
      qtyInput.value = '1';
    }
    
    this.updateCartButton();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    const target = this.closest('.shopify-section, dialog');
    target?.removeEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);

    const { dialog } = this.refs;
    if (dialog && dialog.parentNode === document.body) {
      dialog.remove();
    }
  }

  #onVariantUpdate = (event) => {
    const { variant } = event.detail;
    if (!variant) return;

    this.dataset.variantId = variant.id;
    this.dataset.inventoryQuantity = variant.inventory_quantity;
    this.dataset.isInStock = variant.available ? 'true' : 'false';

    const dialog = this.refs.dialog;
    const variantInput = this.#dialogRefs.variantInput || dialog?.querySelector('input[name="id"]');
    const qtyInput = this.#dialogRefs.qtyInput || dialog?.querySelector('.bulk-pricing-modal__qty-val');

    if (variantInput) {
      variantInput.value = variant.id;
    }

    if (qtyInput) {
      qtyInput.max = String(variant.inventory_quantity);
      if (parseInt(qtyInput.value) > variant.inventory_quantity) {
        qtyInput.value = String(Math.max(1, variant.inventory_quantity));
      }
    }

    try {
      const tiersData = this.dataset.tiers;
      if (tiersData) {
        this.#tiers = JSON.parse(tiersData);
        this.#tiers.sort((a, b) => b.min - a.min);
      }
    } catch (e) {}

    this.updateCartButton();
  };

  decreaseQty() {
    const dialog = this.refs.dialog;
    const qtyInput = this.#dialogRefs.qtyInput || dialog?.querySelector('.bulk-pricing-modal__qty-val');
    if (!qtyInput) return;

    const currentVal = parseInt(qtyInput.value) || 1;
    const newVal = Math.max(1, currentVal - 1);
    qtyInput.value = String(newVal);
    this.updateCartButton();
  }

  increaseQty() {
    const dialog = this.refs.dialog;
    const qtyInput = this.#dialogRefs.qtyInput || dialog?.querySelector('.bulk-pricing-modal__qty-val');
    if (!qtyInput) return;

    const currentVal = parseInt(qtyInput.value) || 1;
    const max = parseInt(qtyInput.max) || Infinity;
    const newVal = Math.min(max, currentVal + 1);
    qtyInput.value = String(newVal);
    this.updateCartButton();
  }

  #getTierForQuantity(qty) {
    for (const tier of this.#tiers) {
      if (qty >= tier.min) {
        return tier;
      }
    }
    return this.#tiers[this.#tiers.length - 1] || null;
  }

  #formatPrice(cents) {
    const dialog = this.refs.dialog;
    const moneyFormat = this.#dialogRefs.moneyFormat || dialog?.querySelector('[ref="moneyFormat"]');
    
    if (moneyFormat instanceof HTMLTemplateElement) {
      const template = moneyFormat.content.textContent || '{{amount}}';
      const amount = (cents / 100).toFixed(2);
      const [whole, decimal] = amount.split('.');
      const formattedWhole = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      
      return template
        .replace(/\{\{\s*amount\s*\}\}/g, `${formattedWhole}.${decimal}`)
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, formattedWhole)
        .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, `${formattedWhole.replace(/,/g, '.')},${decimal}`)
        .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, formattedWhole.replace(/,/g, '.'));
    }
    
    const amountValue = cents / 100;
    const currency = window.Shopify?.currency?.active || 'EUR';
    
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currency,
    }).format(amountValue);
  }

  updateCartButton() {
    const dialog = this.refs.dialog;
    
    const qtyInput = this.#dialogRefs.qtyInput || dialog?.querySelector('.bulk-pricing-modal__qty-val');
    const cartButton = this.#dialogRefs.cartButton || dialog?.querySelector('.bulk-pricing-modal__cart-btn');
    const unitPriceDisplay = this.#dialogRefs.unitPriceDisplay || dialog?.querySelector('[ref="unitPriceDisplay"]');
    const totalPriceDisplay = this.#dialogRefs.totalPriceDisplay || dialog?.querySelector('[ref="totalPriceDisplay"]');

    const addToCartText = window.theme?.strings?.addToCart || 'Add to Cart';
    if (cartButton) {
      cartButton.textContent = addToCartText;
      cartButton.disabled = false;
    }

    if (!qtyInput || !unitPriceDisplay || !totalPriceDisplay) return;

    const qty = parseInt(qtyInput.value) || 1;
    const tier = this.#getTierForQuantity(qty);
    
    if (!tier) return;

    const unitPrice = tier.price;
    const totalPrice = unitPrice * qty;

    unitPriceDisplay.textContent = this.#formatPrice(unitPrice);
    totalPriceDisplay.textContent = this.#formatPrice(totalPrice);
  }

  async addToCart() {
    const dialog = this.refs.dialog;
    const cartButton = this.#dialogRefs.cartButton || dialog?.querySelector('.bulk-pricing-modal__cart-btn');
    const qtyInput = this.#dialogRefs.qtyInput || dialog?.querySelector('.bulk-pricing-modal__qty-val');
    const variantInput = this.#dialogRefs.variantInput || dialog?.querySelector('input[name="id"]');

    if (!cartButton) return;

    const variantId = parseInt(variantInput?.value || this.dataset.variantId || '', 10);
    const quantity = parseInt(qtyInput?.value || '1', 10) || 1;

    if (!variantId || isNaN(variantId)) return;

    cartButton.disabled = true;
    cartButton.textContent = window.theme?.strings?.adding || 'Adding...';

    try {
      const cartAddUrl = window.routes?.cart_add_url || '/cart/add.js';
      
      const response = await fetch(cartAddUrl, {
        ...fetchConfig('json'),
        body: JSON.stringify({
          items: [{ id: variantId, quantity: quantity }],
        }),
      });

      if (!response.ok) throw new Error('Failed to add to cart');

      const responseData = await response.json();

      this.dispatchEvent(
        new CartAddEvent({}, String(variantId), {
          source: 'bulk-pricing-modal',
          itemCount: quantity,
          productId: this.dataset.productId,
          sections: responseData.sections,
        })
      );

      this.closeDialog();
    } catch (error) {
      cartButton.disabled = false;
      cartButton.textContent = window.theme?.strings?.error || 'Error - try again';
      setTimeout(() => {
        this.updateCartButton();
      }, 3000);
    }
  }
}

if (!customElements.get('bulk-pricing-modal')) {
  customElements.define('bulk-pricing-modal', BulkPricingModal);
}

export { BulkPricingModal };
